import { throttle } from "@/lib/utils";
import { CANVAS_INITIALIZED_EVENT, LAYER_CHANGED_EVENT } from "../constants/canvasConstants";
import { BasicExtensionFactory, Canvas, FabricData } from "../types/canvasTypes";
import { getCanvasJsonData } from "../utils/canvasUtils";

const NAME = "data-saver";
export const THROTTLE_TIME = 3000;

export const MODIFICATION_EVENTS: Parameters<Canvas["on"]>[0][] = [
  "object:modified",
  "object:moving",
  "object:scaling",
  "object:rotating",
  "object:skewing",
  "object:resizing",
  "object:added",
  "object:removed",
  LAYER_CHANGED_EVENT,
];

export const createDataSaverExtension: BasicExtensionFactory<{
  handleUpdate: (data: FabricData) => void;
  handleImmediateUpdate: (data: FabricData) => void;
}> = ({ handleUpdate, handleImmediateUpdate }) => {
  let handleAfterRenderer: (() => void) | undefined;
  let subscribeToModificationEvents: (() => void) | undefined;
  let isExtensionDestroyed = false;
  let lastData: FabricData;

  const [onUpdate, markDataChangedForUpdate] = createFunctionsForExecutingAndMarkAsChanged(handleUpdate);
  const [onImmediateUpdate, markDataChangedForImmediateUpdate] =
    createFunctionsForExecutingAndMarkAsChanged(handleImmediateUpdate);

  const markDataChanged = () => {
    markDataChangedForUpdate();
    markDataChangedForImmediateUpdate();
  };

  const throttledSave = throttle((data: FabricData) => {
    if (isExtensionDestroyed) return;
    onUpdate(data);
  }, THROTTLE_TIME);

  return {
    name: NAME,
    onInit: (canvas) => {
      handleAfterRenderer = () => {
        lastData = getCanvasJsonData(canvas);
        throttledSave?.(lastData);
        onImmediateUpdate(lastData);
      };

      subscribeToModificationEvents = () => {
        MODIFICATION_EVENTS.forEach((event) => canvas.on(event, markDataChanged));
      };

      // Subscribe to modification events only after canvas is initialized to avoid extra patch calls on canvas initialization
      canvas.on(CANVAS_INITIALIZED_EVENT, subscribeToModificationEvents);
      canvas.on("after:render", handleAfterRenderer);
    },
    onDestroy: (canvas) => {
      // Before destroy make the final check
      if (lastData) {
        onUpdate(lastData);
      }
      isExtensionDestroyed = true;

      canvas.off(CANVAS_INITIALIZED_EVENT, subscribeToModificationEvents);
      MODIFICATION_EVENTS.forEach((event) => canvas.off(event, markDataChanged));
      canvas.off("after:render", handleAfterRenderer);
    },
  };
};

function createFunctionsForExecutingAndMarkAsChanged(handleUpdate: (data: FabricData) => void) {
  let isDataChanged = false;

  return [
    (data: FabricData) => {
      if (!isDataChanged) return;

      handleUpdate(data);
      isDataChanged = false;
    },
    () => (isDataChanged = true),
  ] as const;
}
